import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { switchMap, tap } from 'rxjs';
import { AuditCollectionResult } from 'src/app/core-module/models/Audits/audit-collection-result';
import { FlattenedQuestionHolder } from 'src/app/core-module/models/Audits/flattened-question-holder';
import { BasicResult } from 'src/app/core-module/models/basic-result';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { AuditService } from 'src/app/core-module/services/audit.service';
import { CustomerContextService } from 'src/app/core-module/services/customer-context.service';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { UserManagementService } from 'src/app/core-module/services/usermanagement.service';
import { UtilService } from 'src/app/core-module/services/util.service';

@Component({
    selector: 'app-select-audit-edit',
    templateUrl: './select-audit-edit.component.html',
    styleUrls: ['./select-audit-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SelectAuditEditComponent {

  public audits: AuditCollectionResult;
  public pendingDelete: number[] = [];
  public deleting: number[] = [];
  public searchText: string = '';
  public selectedAuditTypeId: number = -1;
  public auditTypeOptions: { id: number; description: string }[] = [];
  public auditCategoryFilter: 'all' | 'campaign' | 'event' | 'standard' = 'all';
  public canEditInternalAudits: boolean = false;
  public viewMode: 'active' | 'removed' = 'active';
  public removedAudits: FlattenedQuestionHolder[] = [];
  public loadingRemovedAudits: boolean = false;
  public reactivatingAuditIds = new Set<number>();

  constructor(
    private auditService: AuditService,
    private router: Router,
    public util: UtilService,
    private toastr:ToastrService,
    private userManagementService: UserManagementService,
    private customerContext: CustomerContextService
  ) {

  }

  ngOnInit() {
    this.loadPage();
  }

  loadPage() {
    this.userManagementService.userSettingsPermissions().pipe(
      tap(res => {
        this.canEditInternalAudits = !res.IsError && res.CanEditInternalAudits;
      }),
      switchMap(() => this.auditService.getEditAudits()),
      tap((res:AuditCollectionResult) => {
        this.audits = new AuditCollectionResult(res);
        this.orderAudits();
        this.buildAuditTypeOptions();
        this.loadRemovedAudits();
      })
    ).subscribe();
  }
  loadRemovedAudits() {
    this.loadingRemovedAudits = true;
    this.auditService.getRemovedAudits().pipe(
      tap((res: AuditCollectionResult) => {
        this.removedAudits = (res.Audits ?? [])
          .filter(audit => this.canEditInternalAudits || !this.isInternalAudit(audit))
          .sort((a, b) => a.AuditName.localeCompare(b.AuditName) || a.AuditID - b.AuditID);
        this.loadingRemovedAudits = false;
      })
    ).subscribe({
      error: () => {
        this.loadingRemovedAudits = false;
        this.toastr.addToastr(new Toastr({ Message: 'Unable to load removed audits.', Type: 3 }));
      }
    });
  }
  reactivateAudit(audit: FlattenedQuestionHolder) {
    if (this.reactivatingAuditIds.has(audit.AuditID)) {
      return;
    }
    this.reactivatingAuditIds.add(audit.AuditID);
    this.auditService.reactivateAudit(audit.AuditID).subscribe({
      next: result => {
        this.reactivatingAuditIds.delete(audit.AuditID);
        if (result.IsError) {
          this.toastr.addToastr(new Toastr({ Message: 'Unable to reactivate the audit.', Type: 3 }));
          return;
        }
        this.toastr.addToastr(new Toastr({ Message: 'Audit reactivated. It is now available to edit.', Type: 2 }));
        this.loadPage();
      },
      error: () => {
        this.reactivatingAuditIds.delete(audit.AuditID);
        this.toastr.addToastr(new Toastr({ Message: 'Unable to reactivate the audit.', Type: 3 }));
      }
    });
  }

  selectAudit(audit: FlattenedQuestionHolder) {
    this.router.navigateByUrl('settings/manage/edit/audit', { state: { 'Audit': audit, 'Duplicate': false } });
  }
  duplicateAudit(audit: FlattenedQuestionHolder) {
    this.router.navigateByUrl('settings/manage/edit/audit', { state: { 'Audit': audit, 'Duplicate': true } });
  }
  isInternalAudit(audit: FlattenedQuestionHolder): boolean {
    return !!audit?.AuditEntryOptions?.InternalAudit;
  }
  orderAudits() {
    this.audits.Audits = this.audits.Audits.sort((a, b) => a.AuditName.localeCompare(b.AuditName));
  }
  buildAuditTypeOptions() {
    const types = new Map<number, string>();
    this.audits?.Audits?.forEach(a => {
      const typeId = a?.AuditInfo?.AuditType?.ID;
      const typeDesc = a?.AuditInfo?.AuditType?.Description;
      if (typeId != null && typeDesc && !types.has(typeId)) {
        types.set(typeId, typeDesc);
      }
    });
    this.auditTypeOptions = Array.from(types.entries())
      .map(([id, description]) => ({ id, description }))
      .sort((a, b) => a.description.localeCompare(b.description));
    this.auditTypeOptions.unshift({ id: -1, description: this.customerContext.displayText('All Audit Types') });
  }
  get filteredAudits(): FlattenedQuestionHolder[] {
    const audits = this.audits?.Audits ?? [];
    const query = this.searchText.trim().toLowerCase();
    return audits.filter(a => {
      const nameOk = !query || (a.AuditName ?? '').toLowerCase().includes(query);
      const typeOk = this.selectedAuditTypeId === -1 || a?.AuditInfo?.AuditType?.ID === this.selectedAuditTypeId;
      const internalOk = this.canEditInternalAudits || !this.isInternalAudit(a);
      const isCampaign = !!a?.AuditEntryOptions?.CampaignAudit;
      const isEvent = !!a?.IsEventAudit;
      const categoryOk =
        this.auditCategoryFilter === 'all' ||
        (this.auditCategoryFilter === 'campaign' && isCampaign) ||
        (this.auditCategoryFilter === 'event' && isEvent) ||
        (this.auditCategoryFilter === 'standard' && !isCampaign && !isEvent);
      return nameOk && typeOk && categoryOk && internalOk;
    });
  }
  setDelete(auditID: number) {
    if (this.forDeletion(auditID)) {
      var index = this.pendingDelete.findIndex(e => e == auditID);
      this.pendingDelete.splice(index, 1);
    } else {
      this.pendingDelete.push(auditID)
    }
  }
  forDeletion(auditID: number) {
    if (this.pendingDelete.findIndex(e => auditID == e) == -1) {
      return false;
    }
    return true;
  }


  deleteAudit(auditID: number) {
    this.deleting.push(auditID);
    this.auditService.deleteAudit(auditID).pipe(
      tap((res:BasicResult) => {
        this.resetDeletingStatus(auditID);
        if (res.IsError) {
          this.toastr.addToastr(new Toastr({ Message: 'Unable to remove the audit, please try again later.', Type: 3 }));
          //unable to delete rn
        } else {
          this.toastr.addToastr(new Toastr({ Message: 'Audit successfully removed.', Type: 2 }));
          this.loadPage();
        }
      })
    ).subscribe();
  }
  attemptingDelete(auditID: number) {
    if (this.deleting.findIndex(e => auditID == e) == -1) {
      return false;
    }
    return true;
  }
  resetDeletingStatus(auditID: number) {
    if (this.attemptingDelete(auditID)) {
      var index = this.deleting.findIndex(e => e == auditID);
      this.deleting.splice(index, 1);
    }
  }


  goBack() {
    this.router.navigateByUrl("/settings");
  }
}
